import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { ExpiryDatePicker } from '@shared/components/ui/date-picker';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { MultiSelect } from '@shared/components/ui/MultiSelect';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { useToast } from '@shared/hooks/use-toast';
import { useActivateProductBatches } from '@modules/Batches/hooks/useBatchMutations';
import type { ActivateProductBatchesResponse } from '@modules/Batches/models/Batch';
import { format } from 'date-fns';
import { faPlus, faTrash } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface ProductLocationStock {
  id: number;
  code: string;
  stock: number;
}

export interface ActivateProductBatchesModalRef {
  open: (options: { productId: number; locations: ProductLocationStock[]; onSuccess?: () => void }) => void;
}

interface BatchFormRow {
  key: string;
  batch_number: string;
  expiry_date?: Date;
  location_ids: number[];
}

const createEmptyBatchRow = (): BatchFormRow => ({
  key: crypto.randomUUID(),
  batch_number: '',
  expiry_date: undefined,
  location_ids: [],
});

const BatchTypeSelect = ({
  value,
  onChange,
  id,
}: {
  value: string;
  onChange: (value: string) => void;
  id?: string;
}) => {
  const { t } = useTranslation('createEditProductsModule');

  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger id={id} className="w-[130px]" data-testid="activate-batch-type-select">
        <SelectValue placeholder={t('form.batch_type_placeholder')} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="FIFO">FIFO</SelectItem>
        <SelectItem value="LIFO">LIFO</SelectItem>
        <SelectItem value="FEFO">FEFO</SelectItem>
      </SelectContent>
    </Select>
  );
};

export const ActivateProductBatchesModal = forwardRef<ActivateProductBatchesModalRef>((_, ref) => {
  const { t } = useTranslation('createEditProductsModule');
  const { t: tBatches } = useTranslation('batchesModule');
  const { toast } = useToast();
  const modalRef = useRef<ModalCenterRef>(null);
  const activateBatches = useActivateProductBatches();
  const onSuccessRef = useRef<(() => void) | null>(null);

  const [productId, setProductId] = useState<number | null>(null);
  const [locations, setLocations] = useState<ProductLocationStock[]>([]);
  const [batchType, setBatchType] = useState('FIFO');
  const [batchRows, setBatchRows] = useState<BatchFormRow[]>([createEmptyBatchRow()]);
  const [result, setResult] = useState<ActivateProductBatchesResponse | null>(null);

  const locationsWithStock = useMemo(() => locations.filter((location) => location.stock > 0), [locations]);
  const hasStock = locationsWithStock.length > 0;

  const totalStockToAssign = useMemo(
    () => locationsWithStock.reduce((sum, location) => sum + location.stock, 0),
    [locationsWithStock],
  );

  const assignedLocationIds = useMemo(() => {
    const ids = new Set<number>();
    batchRows.forEach((row) => row.location_ids.forEach((id) => ids.add(id)));
    return ids;
  }, [batchRows]);

  const unassignedLocations = useMemo(
    () => locationsWithStock.filter((location) => !assignedLocationIds.has(location.id)),
    [locationsWithStock, assignedLocationIds],
  );

  const resetForm = () => {
    setProductId(null);
    setLocations([]);
    setBatchType('FIFO');
    setBatchRows([createEmptyBatchRow()]);
    setResult(null);
    onSuccessRef.current = null;
  };

  useImperativeHandle(ref, () => ({
    open: ({ productId: id, locations: locs, onSuccess }) => {
      setProductId(id);
      setLocations(locs);
      setBatchType('FIFO');
      setBatchRows([createEmptyBatchRow()]);
      setResult(null);
      onSuccessRef.current = onSuccess ?? null;
      modalRef.current?.openModal();
    },
  }));

  const handleClose = () => {
    modalRef.current?.closeModal();
    resetForm();
  };

  const updateBatchRow = (key: string, patch: Partial<BatchFormRow>) => {
    setBatchRows((rows) => rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  };

  const getLocationOptionsForRow = useCallback(
    (rowKey: string, rowLocationIds: number[]) => {
      const takenByOthers = new Set<number>();
      batchRows.forEach((row) => {
        if (row.key !== rowKey) {
          row.location_ids.forEach((id) => takenByOthers.add(id));
        }
      });

      return locationsWithStock
        .filter((location) => !takenByOthers.has(location.id) || rowLocationIds.includes(location.id))
        .map((location) => ({
          value: String(location.id),
          label: location.code,
          description: `${location.stock} uds.`,
        }));
    },
    [batchRows, locationsWithStock],
  );

  const setRowLocations = (rowKey: string, locationIds: number[]) => {
    setBatchRows((rows) =>
      rows.map((row) => {
        if (row.key === rowKey) {
          return { ...row, location_ids: locationIds };
        }
        return {
          ...row,
          location_ids: row.location_ids.filter((id) => !locationIds.includes(id)),
        };
      }),
    );
  };

  const validateForm = (): string | null => {
    if (!batchType) {
      return t('activate_batches.errors.batch_type_required');
    }

    if (!hasStock) {
      return null;
    }

    if (batchRows.length === 0) {
      return t('activate_batches.errors.batches_required');
    }

    for (const row of batchRows) {
      if (!row.batch_number.trim()) {
        return t('activate_batches.errors.batch_number_required');
      }
      if (batchType === 'FEFO' && !row.expiry_date) {
        return t('activate_batches.errors.expiry_required_fefo');
      }
      if (row.location_ids.length === 0) {
        return t('activate_batches.errors.locations_required');
      }
    }

    if (unassignedLocations.length > 0) {
      return t('activate_batches.errors.unassigned_locations');
    }

    return null;
  };

  const handleSubmit = async () => {
    if (!productId) return;

    const validationError = validateForm();
    if (validationError) {
      toast({
        title: t('activate_batches.error_title'),
        description: validationError,
        variant: 'destructive',
      });
      return;
    }

    try {
      const payload = hasStock
        ? {
            batch_type: batchType,
            batches: batchRows.map((row) => ({
              batch_number: row.batch_number.trim(),
              expiry_date: row.expiry_date ? format(row.expiry_date, 'yyyy-MM-dd') : undefined,
              location_ids: row.location_ids,
            })),
          }
        : { batch_type: batchType };

      const response = await activateBatches.mutateAsync({ productId, data: payload });
      setResult(response);
      onSuccessRef.current?.();
      toast({
        title: t('activate_batches.success_title'),
        description: t('activate_batches.success_desc', { quantity: response.quantity_assigned }),
        variant: 'success',
      });
    } catch (error: any) {
      toast({
        title: t('activate_batches.error_title'),
        description: error?.response?.data?.message || error?.message || t('activate_batches.error_desc'),
        variant: 'destructive',
      });
    }
  };

  const allResultLocations = useMemo(() => {
    if (!result) return [];
    const map = new Map<number, string>();
    result.batches.forEach((batch) => {
      batch.locations.forEach((location) => {
        map.set(location.location_id, location.location_code);
      });
    });
    return Array.from(map.entries()).map(([id, code]) => ({ id, code }));
  }, [result]);

  return (
    <ModalCenter
      ref={modalRef}
      title={result ? t('activate_batches.result_title') : t('activate_batches.title')}
      type="info"
      width={hasStock ? 'xxl' : 'lg'}
      maxHeight="90vh"
      classNameBody="overflow-y-auto"
      onCloseModal={resetForm}
    >
      {result ? (
        <div className="space-y-4" data-testid="activate-batches-result">
          <div className="flex flex-wrap items-center gap-3">
            <Badge variant="outline">
              {t('activate_batches.result_stock_rows')}: {result.stock_rows_assigned}
            </Badge>
            <Badge variant="outline">
              {t('activate_batches.result_quantity')}: {result.quantity_assigned}
            </Badge>
          </div>

          <div className="overflow-x-auto rounded-lg border border-card-border">
            <table className="w-full min-w-max text-sm">
              <thead>
                <tr className="border-b border-card-border bg-card-bg">
                  <th className="px-4 py-2.5 text-left font-medium text-complementary-text">
                    {t('activate_batches.table.batch')}
                  </th>
                  <th className="px-4 py-2.5 text-right font-medium text-complementary-text">
                    {t('activate_batches.table.total')}
                  </th>
                  {allResultLocations.map((location) => (
                    <th
                      key={location.id}
                      className="px-4 py-2.5 text-right font-medium text-complementary-text whitespace-nowrap"
                    >
                      {location.code}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.batches.map((batch) => (
                  <tr key={batch.batch_id} className="border-b border-card-border last:border-b-0">
                    <td className="px-4 py-2.5 font-medium whitespace-nowrap">{batch.batch_number}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{batch.quantity_assigned}</td>
                    {allResultLocations.map((location) => {
                      const assigned = batch.locations.find((item) => item.location_id === location.id);
                      return (
                        <td key={location.id} className="px-4 py-2.5 text-right tabular-nums text-complementary-text">
                          {assigned ? assigned.quantity_assigned : '—'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end pt-2">
            <Button variant="primary" size="lg" onClick={handleClose} data-testid="activate-batches-close">
              {t('activate_batches.close')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4" data-testid="activate-batches-form">
          <p className="text-sm text-complementary-text">
            {hasStock ? t('activate_batches.description_with_stock') : t('activate_batches.description_no_stock')}
          </p>

          {!hasStock ? (
            <div className="flex items-end gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="activate-batch-type">{t('form.batch_type_label')}</Label>
                <BatchTypeSelect id="activate-batch-type" value={batchType} onChange={setBatchType} />
              </div>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-card-border bg-card-bg px-4 py-3">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="activate-batch-type" className="text-sm whitespace-nowrap mb-0">
                      {t('form.batch_type_label')}:
                    </Label>
                    <BatchTypeSelect id="activate-batch-type" value={batchType} onChange={setBatchType} />
                  </div>
                  <div className="hidden sm:block h-5 w-px bg-card-border" />
                  <div className="flex flex-wrap items-center gap-3 text-sm">
                    <span>
                      <span className="text-complementary-text">{t('activate_batches.stock_to_assign')}: </span>
                      <span className="font-semibold">{totalStockToAssign} uds.</span>
                    </span>
                    <span>
                      <span className="text-complementary-text">{t('activate_batches.locations_assigned')}: </span>
                      <span
                        className={`font-semibold ${unassignedLocations.length > 0 ? 'text-warning' : 'text-success'}`}
                      >
                        {assignedLocationIds.size}/{locationsWithStock.length}
                      </span>
                    </span>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto rounded-lg border border-card-border">
                <table className="w-full min-w-[780px] text-sm">
                  <thead>
                    <tr className="border-b border-card-border bg-card-bg">
                      <th className="w-10 px-3 py-2.5 text-center font-medium text-complementary-text align-bottom">
                        #
                      </th>
                      <th className="min-w-[150px] px-3 py-2.5 text-left font-medium text-complementary-text align-bottom">
                        {t('activate_batches.batch_number_label')}
                      </th>
                      <th className="min-w-[320px] px-3 py-2.5 text-left font-medium text-complementary-text align-bottom">
                        {tBatches('modals.create.expiry_date')}
                        {batchType === 'FEFO' ? ' *' : ''}
                      </th>
                      <th className="min-w-[220px] px-3 py-2.5 text-left font-medium text-complementary-text align-bottom">
                        {t('activate_batches.locations_label')}
                      </th>
                      <th className="w-10 px-3 py-2.5 align-bottom" />
                    </tr>
                  </thead>
                  <tbody>
                    {batchRows.map((row, index) => (
                      <tr key={row.key} className="border-b border-card-border last:border-b-0 hover:bg-hover-press/30">
                        <td className="px-3 py-3 align-bottom text-center text-complementary-text tabular-nums">
                          {index + 1}
                        </td>
                        <td className="px-3 py-3 align-bottom">
                          <Input
                            value={row.batch_number}
                            onChange={(e) => updateBatchRow(row.key, { batch_number: e.target.value })}
                            placeholder={t('activate_batches.batch_number_placeholder')}
                            className="h-10 bg-card-bg"
                            data-testid={`activate-batch-number-${index}`}
                          />
                        </td>
                        <td className="px-3 py-3 align-bottom min-w-[320px]">
                          <div className="[&_span.text-xs.font-medium.text-muted-foreground]:hidden [&_[data-testid=expiry-date-preview]]:hidden">
                            <ExpiryDatePicker
                              date={row.expiry_date}
                              onDateChange={(date) => updateBatchRow(row.key, { expiry_date: date })}
                              fromYear={new Date().getFullYear()}
                              toYear={new Date().getFullYear() + 20}
                              className="space-y-0"
                              labels={{
                                day: tBatches('modals.create.expiry_day'),
                                month: tBatches('modals.create.expiry_month'),
                                year: tBatches('modals.create.expiry_year'),
                                preview: tBatches('modals.create.expiry_preview'),
                                search: tBatches('modals.create.expiry_search'),
                                notFound: tBatches('modals.create.expiry_not_found'),
                              }}
                              data-testid={`activate-batch-expiry-${index}`}
                            />
                          </div>
                        </td>
                        <td className="px-3 py-3 align-bottom">
                          <MultiSelect
                            options={getLocationOptionsForRow(row.key, row.location_ids)}
                            value={row.location_ids.map(String)}
                            onValueChange={(values) =>
                              setRowLocations(
                                row.key,
                                values.map((value) => Number(value)),
                              )
                            }
                            placeholder={t('activate_batches.locations_placeholder')}
                            emptyText={t('activate_batches.locations_empty')}
                            searchPlaceholder={t('activate_batches.locations_search')}
                            dataTestId={`activate-batch-locations-${index}`}
                            triggerClassName="h-10"
                          />
                        </td>
                        <td className="px-3 py-3 align-bottom text-center">
                          {batchRows.length > 1 ? (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-10 w-10 text-complementary-text hover:text-error"
                              onClick={() => setBatchRows((rows) => rows.filter((item) => item.key !== row.key))}
                              data-testid={`activate-batches-remove-${index}`}
                            >
                              <FontAwesomeIcon icon={faTrash} className="w-3.5 h-3.5" />
                            </Button>
                          ) : (
                            <span className="inline-block h-10 w-10" aria-hidden="true" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <button
                type="button"
                onClick={() => setBatchRows((rows) => [...rows, createEmptyBatchRow()])}
                className="inline-flex items-center gap-1.5 text-sm text-complementary-text hover:text-foreground transition-colors px-1 py-1 -ml-1"
                data-testid="activate-batches-add-row"
              >
                <FontAwesomeIcon icon={faPlus} className="w-3 h-3" />
                {t('activate_batches.add_batch')}
              </button>

              {unassignedLocations.length > 0 && (
                <p className="text-sm text-warning" data-testid="activate-batches-unassigned-warning">
                  {t('activate_batches.unassigned_warning', {
                    locations: unassignedLocations.map((location) => location.code).join(', '),
                  })}
                </p>
              )}
            </>
          )}

          <div className="flex justify-end gap-2 border-t border-card-border pt-4">
            <Button variant="outline" size="lg" onClick={handleClose} disabled={activateBatches.isPending}>
              {t('modal.cancelar')}
            </Button>
            <Button
              variant="primary"
              size="lg"
              onClick={handleSubmit}
              disabled={activateBatches.isPending}
              data-testid="activate-batches-submit"
            >
              {activateBatches.isPending ? t('activate_batches.submitting') : t('activate_batches.submit')}
            </Button>
          </div>
        </div>
      )}
    </ModalCenter>
  );
});

ActivateProductBatchesModal.displayName = 'ActivateProductBatchesModal';
